import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { userService } from '../services/user.service';
import { updateUser } from '../store/slices/authSlice';
import { useToast } from '../components/common/Toast';
import { validatePasswordChange } from '../utils/addressValidator';

export const useProfile = () => {
  const dispatch = useDispatch();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const updateProfile = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    try {
      const response = await userService.updateProfile(data);
      dispatch(updateUser(response.data.user));
      toast.success('Profile updated successfully');
      return response;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to update profile';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [dispatch, toast]);

  const changePassword = useCallback(async (currentPassword, newPassword, confirmPassword) => {
    const validation = validatePasswordChange(currentPassword, newPassword, confirmPassword);
    
    if (!validation.isValid) {
      setError('Validation failed');
      return { success: false, errors: validation.errors };
    }

    setLoading(true);
    setError(null);
    try {
      const response = await userService.changePassword(currentPassword, newPassword, confirmPassword);
      toast.success('Password changed successfully');
      return { success: true, data: response };
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to change password';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [toast]);

  return {
    loading,
    error,
    updateProfile,
    changePassword,
  };
};

export default useProfile;